import React, { useState } from 'react';
import { NButton, NDialog, NInput } from '@innersource-nn/novoui-react';
import NSelectTooltip from 'components/Inputs/InputsWithTooltip/NSelectTooltip';
import useCreateOrganization from '../hooks/useCreateOrganization';
import Spinner from 'components/loader/Spinner';
import NInputWithChips from 'components/Inputs/Labels Input/NInputWithChips';
import { ModalProps } from 'types/Modal';
import LastStep from 'components/vertical-stepper/LastStep';
import { useNavigate } from 'react-router-dom';
import NTextAreaQuill from 'components/Inputs/TextAreaQuill/NTextAreaQuill';

const defaultValues = {
    label: '',
    description: '',
    SamlGroupName: '',
    tags: [],
};
const CreateOrganization: React.FC<ModalProps> = ({ visible, onClose }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState(defaultValues);
    const { loading, adGroupOptions, buttonLoading, handleSubmit } =
        useCreateOrganization();

    const [newOrganization, setNewOrganization] = useState({
        organizationUri: '',
        label: '',
    });
    const [creationError, setCreationError] = useState('');

    const [activeStep, setActiveStep] = useState<number>(0);
    const updateLabels = (inputName: string, newValue: string[]) => {
        setFormData((prevData: any) => {
            return {
                ...prevData,
                [inputName]: newValue,
            };
        });
    };

    async function handleClick() {
        const newOrg = await handleSubmit(formData, setCreationError);
        setNewOrganization(newOrg);
        setActiveStep(1);
    }

    const handleClickSuccess = () => {
        navigate(`/organization/${newOrganization.organizationUri}`);
        if (onClose) onClose();
    };
    return (
        visible &&
        onClose && (
            <NDialog
                isVisible={visible}
                closeDialog={onClose}
                backgroundClose
                headline={activeStep === 1 ? '' : 'Create organization'}
                size="medium"
                showDividers={false}
            >
                {loading ? (
                    <Spinner />
                ) : activeStep === 1 ? (
                    newOrganization === null ? (
                        <LastStep
                            title="Organization creation failed"
                            type="error"
                            description={
                                <p>
                                    We encountered an error while creating your
                                    organization. {creationError}
                                </p>
                            }
                            handleCancel={onClose}
                            primaryButtonAction={() => setActiveStep(0)}
                            primaryButtonText="Retry creating organization"
                            secondaryButtonText="Contact support"
                            secondaryButtonAction={() =>
                                window.open(
                                    process.env.REACT_APP_TEAMS_LINK || '',
                                    '_blank',
                                )
                            }
                        />
                    ) : (
                        <LastStep
                            title="Organization created!"
                            description={
                                <p>
                                    Your new organization:{' '}
                                    <b className="font-bold">
                                        {newOrganization.label}
                                    </b>{' '}
                                    has been successfully created
                                </p>
                            }
                            handleCancel={onClose}
                            primaryButtonAction={handleClickSuccess}
                            primaryButtonText="Go to Organization"
                            secondaryButtonText="Close"
                            type="success"
                        />
                    )
                ) : (
                    <div className="flex flex-col gap-16 mt-16">
                        <NInput
                            label="Organization name*"
                            size="medium"
                            variant="outline"
                            placeholder="Maximum 52 characters..."
                            modelValue={formData.label}
                            invalid={formData.label.length > 52}
                            assistiveText={
                                formData.label.length > 52
                                    ? 'Maximum 52 characters are allowed'
                                    : ''
                            }
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    label: e.target.value,
                                })
                            }
                        />
                        <NTextAreaQuill
                            label="Organization description*"
                            variant="outline"
                            placeholder="Please insert a short description which describes what this organization contains and the purpose of the organization..."
                            modelValue={formData.description}
                            onChange={(value) =>
                                setFormData({
                                    ...formData,
                                    description: value,
                                })
                            }
                        />
                        <NSelectTooltip
                            content="Select the domain which is most relevant to the new dataset, if the domain does not exist please contact the datahub support team."
                            label="Organization admin name*"
                            size="medium"
                            variant="outline"
                            placeholder="Select an admin group"
                            options={adGroupOptions}
                            modelValue={formData.SamlGroupName}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    SamlGroupName: e.target.value,
                                })
                            }
                        />
                        <NInputWithChips
                            name="tags"
                            label="Tags"
                            content="Add descriptive tags or keywords to categorize your datasets using meaningful words."
                            placeholder="Type and press enter... "
                            updateLabels={updateLabels}
                            labels={formData.tags}
                            modelValue=""
                        />
                        <div className="flex justify-end gap-8">
                            <NButton variant="outline" square onClick={onClose}>
                                Cancel
                            </NButton>
                            <NButton
                                variant="fill"
                                isLoading={buttonLoading}
                                square
                                className="bg-sea-blue-600"
                                onClick={handleClick}
                                disabled={
                                    formData.label.length > 52 ||
                                    formData.SamlGroupName === '' ||
                                    formData.label === '' ||
                                    formData.description === ''
                                }
                            >
                                Create
                            </NButton>
                        </div>
                    </div>
                )}
            </NDialog>
        )
    );
};
export default CreateOrganization;
